// src/utils/parkingUtils.js

// ======================================================
// DYNAMIC PARKING ENGINE
// ======================================================
//
// FEATURES
// ------------------------------------------------------
// ✅ Uses REAL backend slot coordinates
// ✅ Vehicles park on USER-DRAWN slots
// ✅ No hardcoded SVG coordinates
// ✅ Stable deterministic assignment
// ✅ No overlap
// ✅ Multi-facility safe
// ✅ Real-time websocket safe
// ✅ Overflow handling
// ✅ React-safe architecture
// ✅ Production ready
//
// IMPORTANT
// ------------------------------------------------------
// This version completely replaces the old
// symbolic/static ZONE_A/ZONE_B/ZONE_C logic.
//
// Vehicles will now park ONLY on:
//   slot_center_x
//   slot_center_y
//
// received from backend API.
//
// ======================================================

// ======================================================
// RUNTIME SLOT REGISTRY
// ======================================================

// facilityKey -> slot[]
const facilitySlots = new Map();

// facilityKey -> free slot queue
const freeSlotQueues = new Map();

// facilityKey -> occupied slot ids
const occupiedSlots = new Map();

// vehicleKey -> slot
const assignedPositions = new Map();

// vehicle metadata
const vehicleMetadata = new Map();

// overflow tracker
let overflowCounter = 0;

// ======================================================
// OVERFLOW CONFIG
// ======================================================

const OVERFLOW_START_X = 500;

const OVERFLOW_START_Y = 700;

const OVERFLOW_COLUMNS = 5;

const OVERFLOW_X_GAP = 70;

const OVERFLOW_Y_GAP = 70;

// ======================================================
// BUILD VEHICLE KEY
// ======================================================

const buildVehicleKey = (
    vehicleId,
    facilityKey = "default"
) => {

    return `${facilityKey}:${vehicleId}`;
};

// ======================================================
// CREATE OVERFLOW POSITION
// ======================================================

const createOverflowPosition = () => {

    const index = overflowCounter++;

    return {

        id: `OVERFLOW-${index}`,

        x:
            OVERFLOW_START_X +
            (
                (index % OVERFLOW_COLUMNS) *
                OVERFLOW_X_GAP
            ),

        y:
            OVERFLOW_START_Y +
            (
                Math.floor(
                    index / OVERFLOW_COLUMNS
                ) *
                OVERFLOW_Y_GAP
            ),

        overflow: true
    };
};

// ======================================================
// INITIALIZE FACILITY LAYOUT
// IMPORTANT:
// Call this AFTER fetching layout API
// ======================================================

export const initializeFacilityLayout = ({
    facilityKey = "default",
    slots = []
}) => {

    // ==============================================
    // SORT SLOTS
    // deterministic ordering
    // ==============================================

    const normalizedSlots = [...slots]

        .filter(
            (slot) =>
                slot.slot_center_x !== undefined &&
                slot.slot_center_y !== undefined
        )

        .sort((a, b) => {

            if (
                a.slot_center_y ===
                b.slot_center_y
            ) {

                return (
                    a.slot_center_x -
                    b.slot_center_x
                );
            }

            return (
                a.slot_center_y -
                b.slot_center_y
            );
        })

        .map((slot) => ({

            id:
                String(
                    slot.slot_id ||
                    slot.slot_code ||
                    crypto.randomUUID()
                ),

            slot_code:
                slot.slot_code,

            x:
                Number(slot.slot_center_x),

            y:
                Number(slot.slot_center_y),

            width:
                slot.width || 70,

            height:
                slot.height || 35,

            rotation:
                slot.rotation || 0,

            polygon_points:
                slot.polygon_points || [],

            overflow: false
        }));

    facilitySlots.set(
        facilityKey,
        normalizedSlots
    );

    freeSlotQueues.set(
        facilityKey,
        normalizedSlots.map(
            (slot) => slot.id
        )
    );

    occupiedSlots.set(
        facilityKey,
        new Set()
    );
};

// ======================================================
// GET SLOT BY ID
// ======================================================

const getSlotById = (
    facilityKey,
    slotId
) => {

    const slots =
        facilitySlots.get(
            facilityKey
        ) || [];

    return slots.find(
        (slot) =>
            String(slot.id) === String(slotId)
    );
};

// ======================================================
// GET AVAILABLE POSITION
// ======================================================

export const getAvailableParkingPosition = ({
    vehicleId,
    facilityKey = "default"
}) => {

    const vehicleKey =
        buildVehicleKey(
            vehicleId,
            facilityKey
        );

    // ==============================================
    // RETURN EXISTING POSITION
    // ==============================================

    if (
        assignedPositions.has(
            vehicleKey
        )
    ) {

        return assignedPositions.get(
            vehicleKey
        );
    }

    // ==============================================
    // GET FREE SLOT QUEUE
    // ==============================================

    const queue =
        freeSlotQueues.get(
            facilityKey
        ) || [];

    const occupied =
        occupiedSlots.get(
            facilityKey
        ) || new Set();

    // ==============================================
    // NO SLOTS
    // ==============================================

    if (!queue.length) {

        const overflow =
            createOverflowPosition();

        assignedPositions.set(
            vehicleKey,
            overflow
        );

        return overflow;
    }

    // ==============================================
    // ASSIGN SLOT
    // ==============================================

    const slotId =
        queue.shift();

    occupied.add(slotId);

    const slot =
        getSlotById(
            facilityKey,
            slotId
        );

    if (!slot) {

        const overflow =
            createOverflowPosition();

        assignedPositions.set(
            vehicleKey,
            overflow
        );

        return overflow;
    }

    assignedPositions.set(
        vehicleKey,
        slot
    );

    vehicleMetadata.set(
        vehicleKey,
        {
            assignedAt: Date.now(),
            lastSeen: Date.now()
        }
    );

    return slot;
};

// ======================================================
// RELEASE POSITION
// ======================================================

export const releaseParkingPosition = ({
    vehicleId,
    facilityKey = "default"
}) => {

    const vehicleKey =
        buildVehicleKey(
            vehicleId,
            facilityKey
        );

    const assignedSlot =
        assignedPositions.get(
            vehicleKey
        );

    if (!assignedSlot) {
        return;
    }

    if (!assignedSlot.overflow) {

        const occupied =
            occupiedSlots.get(
                facilityKey
            );

        const queue =
            freeSlotQueues.get(
                facilityKey
            );

        if (occupied) {

            occupied.delete(
                assignedSlot.id
            );
        }

        if (queue) {

            queue.unshift(
                assignedSlot.id
            );
        }
    }

    assignedPositions.delete(
        vehicleKey
    );

    vehicleMetadata.delete(
        vehicleKey
    );
};

// ======================================================
// RESET FACILITY LAYOUT
// ======================================================

export const resetParkingLayout = (
    facilityKey = null
) => {

    // ==============================================
    // RESET SINGLE FACILITY
    // ==============================================

    if (facilityKey) {

        facilitySlots.delete(
            facilityKey
        );

        freeSlotQueues.delete(
            facilityKey
        );

        occupiedSlots.delete(
            facilityKey
        );

        return;
    }

    // ==============================================
    // RESET EVERYTHING
    // ==============================================

    facilitySlots.clear();

    freeSlotQueues.clear();

    occupiedSlots.clear();

    assignedPositions.clear();

    vehicleMetadata.clear();

    overflowCounter = 0;
};

// ======================================================
// MAP API VEHICLE
// ======================================================

const occupySlot = (
    facilityKey,
    slotId
) => {

    const occupied =
        occupiedSlots.get(
            facilityKey
        );

    const queue =
        freeSlotQueues.get(
            facilityKey
        );

    const normalizedSlotId =
        String(slotId);

    if (occupied) {

        occupied.add(normalizedSlotId);
    }

    if (queue) {

        const index = queue.indexOf(
            normalizedSlotId
        );

        if (index !== -1) {

            queue.splice(index, 1);
        }
    }
};

const findSlotByCoordinates = (
    facilityKey,
    x,
    y
) => {

    const slots =
        facilitySlots.get(
            facilityKey
        ) || [];

    return slots.find(
        (slot) =>
            slot.x === x &&
            slot.y === y
    );
};

export const mapVehicleFromAPI = (
    session,
    facilityKeyOverride = null
) => {

    const vehicleId =

        session.id ||

        session.canonical_session_key ||

        crypto.randomUUID();

    const facilityKey =

        facilityKeyOverride ||

        session.facility_key ||

        "default";

    // ==============================================
    // USE ACTUAL SLOT POSITION FROM API
    // ==============================================

    let position = null;

    if (
        session.slot_center_x !== undefined &&
        session.slot_center_y !== undefined
    ) {

        const x =
            Number(
                session.slot_center_x
            );

        const y =
            Number(
                session.slot_center_y
            );

        let slot = null;

        if (session.slot_id) {
            slot = getSlotById(
                facilityKey,
                session.slot_id
            );
        }

        if (!slot) {
            slot = findSlotByCoordinates(
                facilityKey,
                x,
                y
            );
        }

        if (slot) {

            position = {

                id: slot.id,

                x: slot.x,

                y: slot.y,

                rotation:
                    slot.rotation || 0,

                overflow: false,

                slot_code:
                    slot.slot_code,

                slot_center_x:
                    slot.x,

                slot_center_y:
                    slot.y,

                polygon_points:
                    slot.polygon_points
            };

            occupySlot(
                facilityKey,
                slot.id
            );

        } else {

            position = {

                id:
                    session.slot_id ||

                    crypto.randomUUID(),

                x,

                y,

                rotation:
                    session.rotation || 0,

                overflow: false
            };
        }

        assignedPositions.set(
            buildVehicleKey(
                vehicleId,
                facilityKey
            ),
            position
        );

    } else if (
        session.slot_id
    ) {

        // ==============================================
        // LOOKUP SLOT BY ID IF COORDINATES NOT PROVIDED
        // ==============================================

        const slot = getSlotById(
            facilityKey,
            session.slot_id
        );

        if (slot) {

            position = {

                id: slot.id,

                x: slot.x,

                y: slot.y,

                rotation:
                    slot.rotation || 0,

                overflow: false
            };

            occupySlot(
                facilityKey,
                slot.id
            );

            assignedPositions.set(
                buildVehicleKey(
                    vehicleId,
                    facilityKey
                ),
                position
            );

        } else {

            // slot not found, fallback
            position =
                getAvailableParkingPosition({

                    vehicleId,

                    facilityKey
                });
        }

    } else {

        // fallback: no coordinates and no slot_id
        position =
            getAvailableParkingPosition({

                vehicleId,

                facilityKey
            });
    }

    return {

        id: vehicleId,

        plate:

            session.license_plate ||

            "UNKNOWN",

        entry_date_key:
            session.entry_date_key || null,

        entry_time_key:
            session.entry_time_key || null,

        x: position.x,

        y: position.y,

        rotation:
            position.rotation || 0,

        status: "parked",

        overflow:
            position.overflow || false,

        slot_id:
            position.id,

        facility_key:
            facilityKey
    };
};

// ======================================================
// CREATE MOVING VEHICLE
// ======================================================

export const createMovingVehicle = (
    eventData,
    facilityKeyOverride = null
) => {

    const vehicleId =

        eventData.id ||

        eventData.canonical_session_key ||

        crypto.randomUUID();

    const facilityKey =

        facilityKeyOverride ||

        eventData.facility_key ||

        "default";

    let position = null;

    if (
        eventData.slot_center_x !== undefined &&
        eventData.slot_center_y !== undefined
    ) {
        const x = Number(eventData.slot_center_x);
        const y = Number(eventData.slot_center_y);

        let slot = null;

        if (eventData.slot_id) {
            slot = getSlotById(
                facilityKey,
                eventData.slot_id
            );
        }

        if (!slot) {
            slot = findSlotByCoordinates(
                facilityKey,
                x,
                y
            );
        }

        if (slot) {
            occupySlot(
                facilityKey,
                slot.id
            );

            position = {
                id: slot.id,
                x: slot.x,
                y: slot.y,
                rotation: slot.rotation || 0,
                overflow: false,
                slot_code: slot.slot_code,
                slot_center_x: slot.x,
                slot_center_y: slot.y,
                polygon_points: slot.polygon_points
            };
        } else {
            position = {
                id:
                    eventData.slot_id ||
                    crypto.randomUUID(),
                x,
                y,
                rotation:
                    eventData.rotation || 0,
                overflow: false
            };
        }

        assignedPositions.set(
            buildVehicleKey(
                vehicleId,
                facilityKey
            ),
            position
        );
    } else if (
        eventData.slot_id
    ) {
        const slot = getSlotById(
            facilityKey,
            eventData.slot_id
        );

        if (slot) {
            occupySlot(
                facilityKey,
                slot.id
            );

            position = {
                id: slot.id,
                x: slot.x,
                y: slot.y,
                rotation: slot.rotation || 0,
                overflow: false,
                slot_code: slot.slot_code,
                slot_center_x: slot.x,
                slot_center_y: slot.y,
                polygon_points: slot.polygon_points
            };

            assignedPositions.set(
                buildVehicleKey(
                    vehicleId,
                    facilityKey
                ),
                position
            );
        }
    }

    if (!position) {
        position = getAvailableParkingPosition({
            vehicleId,
            facilityKey
        });
    }

    return {

        id: vehicleId,

        plate:

            eventData.license_plate ||

            "UNKNOWN",

        entry_date_key:
            eventData.entry_date_key || null,

        entry_time_key:
            eventData.entry_time_key || null,

        x: position.x,

        y: position.y,

        rotation:
            position.rotation || 0,

        status: "moving",

        overflow:
            position.overflow || false,

        slot_id:
            position.id,

        slot_code:
            position.slot_code,

        slot_center_x:
            position.x,

        slot_center_y:
            position.y,

        polygon_points:
            position.polygon_points,

        facility_key:
            facilityKey
    };
};

// ======================================================
// CREATE EXIT VEHICLE
// ======================================================

export const createExitVehicle = (
    eventData,
    facilityKeyOverride = null
) => {

    const vehicleId =

        eventData.id ||

        eventData.canonical_session_key;

    const facilityKey =

        facilityKeyOverride ||

        eventData.facility_key ||

        "default";

    const vehicleKey =
        buildVehicleKey(
            vehicleId,
            facilityKey
        );

    const existingPosition =
        assignedPositions.get(
            vehicleKey
        );

    return {

        id: vehicleId,

        plate:

            eventData.license_plate ||

            "UNKNOWN",

        entry_date_key:
            eventData.entry_date_key || null,

        entry_time_key:
            eventData.entry_time_key || null,

        x:
            existingPosition?.x || 950,

        y:
            existingPosition?.y || 400,

        rotation:
            existingPosition?.rotation || 0,

        status: "exiting",

        facility_key:
            facilityKey
    };
};

// ======================================================
// FINALIZE EXIT
// ======================================================

export const finalizeVehicleExit = ({
    vehicleId,
    facilityKey = "default"
}) => {

    releaseParkingPosition({

        vehicleId,

        facilityKey
    });
};

// ======================================================
// TOUCH SESSION
// ======================================================

export const touchVehicleSession = ({
    vehicleId,
    facilityKey = "default"
}) => {

    const vehicleKey =
        buildVehicleKey(
            vehicleId,
            facilityKey
        );

    const metadata =
        vehicleMetadata.get(
            vehicleKey
        );

    if (!metadata) {
        return;
    }

    metadata.lastSeen =
        Date.now();
};

// ======================================================
// CLEAN STALE VEHICLES
// ======================================================

export const cleanupStaleVehicles = ({
    maxAgeMs =
        30 * 60 * 1000
} = {}) => {

    const now = Date.now();

    vehicleMetadata.forEach(
        (
            metadata,
            vehicleKey
        ) => {

            if (
                now -
                metadata.lastSeen >
                maxAgeMs
            ) {

                const [
                    facilityKey,
                    vehicleId
                ] =
                    vehicleKey.split(":");

                releaseParkingPosition({

                    vehicleId,

                    facilityKey
                });
            }
        }
    );
};

// ======================================================
// ENTRY GATE
// ======================================================

export const getEntryGatePosition =
    () => {

        return {

            x: 90,

            y: 400
        };
    };

// ======================================================
// EXIT GATE
// ======================================================

export const getExitGatePosition =
    () => {

        return {

            x: 1180,

            y: 400
        };
    };

// ======================================================
// UTILIZATION
// ======================================================

export const getParkingUtilization = (
    facilityKey = "default"
) => {

    const occupied =
        occupiedSlots.get(
            facilityKey
        )?.size || 0;

    const total =
        facilitySlots.get(
            facilityKey
        )?.length || 0;

    return {

        occupied,

        total,

        percentage:

            total > 0

                ? Math.round(
                    (
                        occupied /
                        total
                    ) * 100
                )

                : 0
    };
};

// ======================================================
// DEBUG HELPERS
// ======================================================

export const getAllSlots = (
    facilityKey = "default"
) => {

    return (
        facilitySlots.get(
            facilityKey
        ) || []
    );
};

export const getAssignedVehicleCount =
    () =>
        assignedPositions.size;

export const getFreeSlotCount = (
    facilityKey = "default"
) => {

    return (
        freeSlotQueues.get(
            facilityKey
        )?.length || 0
    );
};